import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { saveStoreProductMediaGalleryApi, updateQuoteProListingApi } from 'api';
import { imgBaseUrl } from 'configs';
import { BackdropCircular } from 'components';


const ProductImageEditor = ({product, media, open, setOpen, isQuote }) => {
  const dispatch = useDispatch();

  const { stores = [] } = useSelector((state) => state.user.profile);
  const { savingProductMediaGallery } = useSelector((state) => state.products);
  const { quoteProductLoading, savingProductLoading } = useSelector((state) => state.quote);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;
  const {id, file, media_type, position, label, types, disabled} = media;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="edit-image-dialog"
      fullWidth
      maxWidth={'md'}
      className="p-4"
    >
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <img
              src={`${mediaStoreUrl}${file}`}
              alt="image"
              className="max-w-full"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Formik
              initialValues={{
                id,
                file,
                media_type,
                position,
                label,
                types,
                disabled
              }}

              validationSchema={
                yup.object({
                  label: yup
                    .string()
                    .required('Field is required')
                })
              }

              onSubmit={async (values) => {
                const rolesToUpdate = values.types;
                const media_gallery_entries = product.media_gallery_entries.map((item) => {
                  return item.id === values.id
                    ? values
                    : {
                      ...item,
                      types: item.types.filter((type) => !rolesToUpdate.includes(type))
                    };
                });

                const productToUpdate = {
                  id: product.id,
                  sku: product.sku,
                  media_gallery_entries
                };

                if (isQuote) {
                  await updateQuoteProListingApi({dispatch, product: productToUpdate});
                } else {
                  await saveStoreProductMediaGalleryApi({dispatch, product: productToUpdate});
                }

                setOpen(false);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue
              }) => (
                <form
                  noValidate
                  autoComplete="off"
                  className="flex-column flex-grow h-full justify-between"
                  onSubmit={handleSubmit}
                >
                  <Typography variant="h4" className="py-4">{label}</Typography>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="label"
                    label="Alt Text"
                    className="my-4"
                    value={values.label}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.label && Boolean(errors.label)}
                    helperText={touched.label && errors.label}
                  />
                  <div className="my-4">
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Role:</FormLabel>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.types.includes('image')}
                              onChange={(event) => {
                                event.target.checked
                                  ? setFieldValue('types', [...values.types, 'image'])
                                  : setFieldValue('types', values.types.filter((type) => type !== 'image'));
                              }}
                              name="image"
                              color="primary"
                            />
                          }
                          label="Base"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.types.includes('small_image')}
                              onChange={(event) => {
                                event.target.checked
                                  ? setFieldValue('types', [...values.types, 'small_image'])
                                  : setFieldValue('types', values.types.filter((type) => type !== 'small_image'));
                              }}
                              name="small_image"
                              color="primary"
                            />
                          }
                          label="Small"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.types.includes('thumbnail')}
                              onChange={(event) => {
                                event.target.checked
                                  ? setFieldValue('types', [...values.types, 'thumbnail'])
                                  : setFieldValue('types', values.types.filter((type) => type !== 'thumbnail'));
                              }}
                              name="thumbnail"
                              color="primary"
                            />
                          }
                          label="Thumbnail"
                        />
                      </FormGroup>
                    </FormControl>
                  </div>
                  <div>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.disabled}
                          onChange={(event) => setFieldValue('disabled', event.target.checked)}
                          name="disabled"
                          color="primary"
                        />
                      }
                      label={isQuote ? 'Hide from quote page' : 'Hide from product page'}
                    />
                  </div>
                  <div className="flex my-4">
                    <Button
                      variant="outlined"
                      className="mr-2"
                      fullWidth
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      fullWidth
                    >
                      Confirm
                    </Button>
                  </div>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </DialogContent>
      <BackdropCircular loading={savingProductMediaGallery || savingProductLoading || quoteProductLoading} />
    </Dialog>
  );
};

export default ProductImageEditor;
